/**
 * Seating layout styles for Advanced Mode (weddings & events).
 * Visual-only: seat keys stay row-N-M / table-N-M for DnD + save.
 *
 * Numbering conventions:
 * - Theater / performance (row styles): Row → Seat; Row 1 nearest stage.
 * - Arena: Section → Row → Seat; Row 1 nearest stage within each section.
 * - Dining (table styles): Table → Seat.
 */

import { parseCapacity } from "../../utils/capacityLimits";

export const SEATING_STYLES = [
  {
    id: "classic",
    name: "Classic",
    appliesTo: "both",
    tag: "Hybrid",
    blurb: "Straight rows and round tables on one shared floor.",
  },
  {
    id: "traditional",
    name: "Traditional Theater",
    appliesTo: "row",
    tag: "Ceremony",
    blurb: "Stage at the front with a center aisle between two blocks.",
  },
  {
    id: "ceremony",
    name: "Wedding Aisle",
    appliesTo: "row",
    tag: "Ceremony",
    blurb: "Classic wedding setup — altar ahead, aisle for the processional.",
  },
  {
    id: "proscenium",
    name: "Proscenium",
    appliesTo: "row",
    tag: "Ceremony",
    blurb: "Curved stage at the front; Row 1 closest, rows fan outward.",
  },
  {
    id: "traverse",
    name: "Traverse / Runway",
    appliesTo: "row",
    tag: "Show",
    blurb: "Center runway with guests seated on both long sides.",
  },
  {
    id: "arena",
    name: "Arena / In-the-round",
    appliesTo: "row",
    tag: "Ceremony",
    blurb: "Center stage with sections; Row 1 nearest the stage in each section.",
  },
  {
    id: "banquet",
    name: "Banquet Rounds",
    appliesTo: "table",
    tag: "Reception",
    blurb: "Circular guest tables — ideal for dinner receptions.",
  },
  {
    id: "long-table",
    name: "Family Long Tables",
    appliesTo: "table",
    tag: "Reception",
    blurb: "Long rectangular tables — Table → Seat dining layout.",
  },
  {
    id: "cabaret",
    name: "Cabaret",
    appliesTo: "table",
    tag: "Reception",
    blurb: "Round tables with seats on ~¾ of the rim; open side faces the stage.",
  },
  {
    id: "head-table",
    name: "Head Table / Wedding Stage",
    appliesTo: "table",
    tag: "Reception",
    blurb: "VIP head table facing the stage, with guest tables beyond.",
  },
  {
    id: "u-shape",
    name: "U-Shape",
    appliesTo: "both",
    tag: "Hybrid",
    blurb: "Open U for speeches, toasts, or intimate dinners.",
  },
  {
    id: "courtyard",
    name: "Courtyard Hybrid",
    appliesTo: "both",
    tag: "Hybrid",
    blurb: "Ceremony aisle up front with banquet tables beyond.",
  },
];

export const getSeatingStyle = (id, customStyles = []) => {
  const custom = (customStyles || []).find((s) => s && s.id === id);
  if (custom) return { ...custom, isCustom: true, tag: custom.tag || "Custom" };
  return SEATING_STYLES.find((s) => s.id === id) || SEATING_STYLES[0];
};

export const stylesForAllocation = (allocationType, customStyles = []) => {
  const type = String(allocationType || "").toLowerCase();
  const presets = SEATING_STYLES.filter((style) => {
    if (style.appliesTo === "both") return true;
    if (type === "both") return true;
    return style.appliesTo === type;
  });
  const customs = (customStyles || [])
    .filter(Boolean)
    .map((s) => ({
      ...s,
      isCustom: true,
      tag: s.tag || "Custom",
      blurb: s.blurb || "Your saved floor plan for this occasion.",
      appliesTo: s.appliesTo || "both",
    }));
  return [...presets, ...customs];
};

const toCount = (value) => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
};

const fillUnits = (unitSize, configuredUnits, budget) => {
  const size = Math.max(0, Number(unitSize) || 0);
  if (size <= 0 || budget <= 0) return [];
  const counts = [];
  let left = budget;
  const maxConfigured = Math.max(0, Number(configuredUnits) || 0);
  const maxUnits = maxConfigured || Math.ceil(budget / size);
  while (left > 0 && counts.length < maxUnits) {
    const take = Math.min(size, left);
    counts.push(take);
    left -= take;
  }
  while (left > 0) {
    const take = Math.min(size, left);
    counts.push(take);
    left -= take;
  }
  return counts;
};

/** Seat counts for the board: layout style can change, seat total follows occasion capacity. */
export const buildOccasionSeatPlan = (occasion) => {
  if (!occasion) return null;
  const typeRaw = String(occasion.seatAllocationType || "both")
    .toLowerCase()
    .trim();
  const type =
    typeRaw === "row" || typeRaw === "table" ? typeRaw : "both";
  const capacity = parseCapacity(occasion.OccasionCapacity);
  let seatsPerRow = toCount(occasion.numberOfSeatsPerRow);
  let seatsPerTable = toCount(occasion.numberOfSeatsPerTable);
  let rows = toCount(occasion.numberOfRows);
  let tables = toCount(occasion.numberOfTables);

  if (type === "row") {
    tables = 0;
    seatsPerTable = 0;
  }
  if (type === "table") {
    rows = 0;
    seatsPerRow = 0;
  }
  if (type !== "table" && !seatsPerRow) seatsPerRow = 10;
  if (type !== "row" && !seatsPerTable) seatsPerTable = 8;

  let rowCounts = [];
  let tableCounts = [];

  if (capacity != null && capacity > 0) {
    if (type === "row") {
      rowCounts = fillUnits(seatsPerRow, rows, capacity);
    } else if (type === "table") {
      tableCounts = fillUnits(seatsPerTable, tables, capacity);
    } else {
      const plannedRow = rows * seatsPerRow;
      const plannedTable = tables * seatsPerTable;
      const planned = plannedRow + plannedTable;
      let rowBudget = capacity;
      let tableBudget = 0;
      if (planned > 0) {
        rowBudget = Math.round((plannedRow / planned) * capacity);
        rowBudget = Math.min(capacity, Math.max(0, rowBudget));
        tableBudget = capacity - rowBudget;
      }
      rowCounts = fillUnits(
        seatsPerRow,
        rows || Math.ceil((rowBudget || 0) / seatsPerRow),
        rowBudget
      );
      tableCounts = fillUnits(
        seatsPerTable,
        tables || Math.ceil((tableBudget || 0) / seatsPerTable),
        tableBudget
      );
    }
  } else {
    rowCounts =
      type === "table"
        ? []
        : Array.from({ length: Math.max(rows, 1) }, () => seatsPerRow);
    tableCounts =
      type === "row"
        ? []
        : Array.from({ length: Math.max(tables, 1) }, () => seatsPerTable);
  }

  return {
    type,
    capacity,
    seatsPerRow,
    seatsPerTable,
    rows: rowCounts.length,
    tables: tableCounts.length,
    rowCounts,
    tableCounts,
    totalSeats:
      rowCounts.reduce((sum, n) => sum + n, 0) +
      tableCounts.reduce((sum, n) => sum + n, 0),
  };
};
export const preferredViewForStyle = (style, allocationType) => {
  if (!style) return null;
  const type = String(allocationType || "").toLowerCase();
  if (type === "both" && style.appliesTo === "both") return "both";
  if (style.appliesTo === "row") return "row";
  if (style.appliesTo === "table") return "table";
  return null;
};
